import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { environment } from 'environments/environment';

/**
 * Account profile, preferences and sessions live on the IAM portal (`/me/*`),
 * not on the tenant app — the sidebar already links "Security & sessions"
 * there. Deep links / bookmarks to the same paths on this app used to 404
 * (kanban #223), so send them to the matching IAM page instead.
 *
 * The route's `data.iamPath` names the target page under the IAM `/me`.
 */
export const iamPortalRedirectGuard: CanActivateFn = (route) => {
    const base = environment.iamPortalUrl.replace(/\/$/, '');
    const iamPath = String(route.data['iamPath'] ?? '');
    inject(DOCUMENT).location.assign(`${base}/me${iamPath}`);
    return false;
};

/**
 * Admin surfaces owned by the IAM portal (`/users`, `/roles`) that tenant-app
 * users still reach through bookmarks or the old fe-web URL (`/users`, `/rbac`).
 * Without a route they fell into the in-shell 404 for every role — the URL
 * stayed put, so nobody could tell "does not exist" from "not allowed" (kanban
 * #5782/#5791). Declare `data.anyRoles` next to this guard: the shell's
 * RouteDataAuthorizationGuard turns a missing role into `/forbidden` before
 * this runs, so only roles the IAM page admits are handed off.
 *
 * The route's `data.iamAdminPath` names the target path on the IAM portal.
 */
export const iamPortalAdminRedirectGuard: CanActivateFn = (route) => {
    const base = environment.iamPortalUrl.replace(/\/$/, '');
    const iamAdminPath = String(route.data['iamAdminPath'] ?? '');
    inject(DOCUMENT).location.assign(`${base}${iamAdminPath}`);
    return false;
};
